import '/src/assets/sass/main.scss'
import 'floating-vue/dist/style.css'

import { createHead } from '@vueuse/head'
import FloatingVue from 'floating-vue'
import { createPinia } from 'pinia'
import { createApp, defineAsyncComponent } from 'vue'

import BrowserService from '@/services/browserService'

import App from './App.vue'
import { i18n } from './mixins/localization'
import router from './routes'
import * as Sentry from "@sentry/vue";
import axios from 'axios'
import axiosRetry from "axios-retry"

// retry every request up to 3 times
axiosRetry(axios, {
  retryDelay: (retryCount: number) => {
    switch (retryCount) {
      case 1: return 0;
      case 2: return 1_000;
      default: return 5_000;
    }
  }
})

const FooterBase = defineAsyncComponent(() => import('./components/footer/FooterBase.vue'))

const app = createApp(App)
  .use(createPinia())
  .use(i18n)
  .use(createHead())
  .use(router)
  .use(FloatingVue, {
    themes: {
      navMenu: {
        $extend: 'dropdown',
        triggers: ['click', 'hover'],
        noAutoFocus: true,
        popperTriggers: ['click'],
        distance: 12
      },
      navItem: {
        $extend: 'dropdown',
        distance: 12,
      },
      usermenu: {
        $extend: 'dropdown',
        triggers: ['click', 'hover', 'focus'],
        noAutoFocus: true,
        popperTriggers: ['click', 'hover'],
      },
      navCart: {
        $extend: 'dropdown',
        triggers: ['click', 'hover', 'focus'],
        noAutoFocus: true,
        popperTriggers: ['click', 'hover'],
      },
      navbarMainMenu: {
        $extend: 'dropdown',
        triggers: ['click', 'hover', 'focus'],
        noAutoFocus: true,
        popperTriggers: ['click', 'hover'],
      },
      search: {
        $extend: 'dropdown',
        triggers: ['focus'],
        showTriggers: ['click'],
        autoHide: false,
        noAutoFocus: true,
      },
      searchMobile: {
        $extend: 'dropdown',
        triggers: ['click', 'focus'],
        noAutoFocus: true,
      },
      productVariants: {
        $extend: 'dropdown',
        triggers: ['click', 'focus'],
        noAutoFocus: true,
        popperTriggers: ['click'],
      },
      nutritiontable: {
        $extend: 'dropdown',
        triggers: ['click', 'focus'],
        noAutoFocus: true,
        popperTriggers: ['click'],
      },
      customerAddresses: {
        $extend: 'dropdown',
        triggers: ['click'],
        noAutoFocus: true,
        popperTriggers: ['click'],
      },
      baseTooltip: {
        $extend: 'tooltip',
        triggers: ['click', 'hover'],
        noAutoFocus: true,
      },
      heroTooltip: {
        $extend: 'tooltip',
        triggers: ['click', 'hover'],
        noAutoFocus: true,
      },
      verifiedUserTooltip: {
        $extend: 'tooltip',
        triggers: ['click', 'hover'],
        noAutoFocus: true,
      },
      checkoutTooltip: {
        $extend: 'tooltip',
        triggers: ['click', 'hover'],
        noAutoFocus: true,
      },
      badgeTooltip: {
        $extend: 'tooltip',
        triggers: ['hover', 'click'],
        noAutoFocus: true,
        placement: 'auto',
      },
    },
  })

if (import.meta.env.VITE_VUE_APP_ENV === "production") {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_VUE_APP_SENTRY_DSN,
    sendDefaultPii: true,
    environment: import.meta.env.VITE_VUE_APP_COUNTRY,
  });
}

app.mount('#app')

if (!BrowserService.isMobileWebview()) {
  const footer = createApp(FooterBase).use(i18n).use(router)
  footer.mount('#footer-wrapper')
}
